import { useState, useEffect, useCallback } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { Alert } from '@material-ui/lab';
import { useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import FilterLine from '../../../components/filter/FilterLine';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import EmaillogTable from './EmaillogTable';
import useDebounce from '../../../hooks/useDebounce';

const EMAIL_LOG_FILTERS = [
    {
        key: 'fromDate',
        rangeKeys: ['fromDate', 'toDate'],
        label: 'Sent date',
        type: 'daterange'
    },
    {
        key: 'to',
        label: 'To',
        type: 'text'
    },
    {
        key: 'subject',
        label: 'Subject',
        type: 'text'
    },
    {
        key: 'result',
        label: 'Result',
        type: 'resultSelect'
    },
    {
        key: 'emailStatus',
        label: 'Email Status',
        type: 'emailStatusSelect'
    },
    {
        key: 'logType',
        label: 'Log Type',
        type: 'emailLogTypeSelect'
    },
    {
        key: 'reservationId',
        label: 'Reservation ID',
        type: 'text'
    }
];

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart.trim() !== '' ? urlSearchPart : '');
    const fromDate =
        searchParams.get('datefrom') !== null && moment(searchParams.get('datefrom')).isValid()
            ? moment(searchParams.get('datefrom'))
            : null;
    const toDate =
        searchParams.get('dateto') !== null && moment(searchParams.get('dateto')).isValid()
            ? moment(searchParams.get('dateto'))
            : null;

    return {
        fromDate,
        toDate,
        to: searchParams.get('to') || '',
        subject: searchParams.get('subject') || '',
        result: searchParams.get('result') || '',
        emailStatus: searchParams.get('emailStatus') || '',
        logType: searchParams.get('logType') || '',
        reservationId: searchParams.get('reservationId') || ''
    };
}

function getUrlFromFilter(filter) {
    const search = new URLSearchParams();

    if (filter.fromDate) {
        search.append('datefrom', filter.fromDate.format('YYYY-MM-DD'));
    }
    if (filter.toDate) {
        search.append('dateto', filter.toDate.format('YYYY-MM-DD'));
    }
    if (filter.to) {
        search.append('to', filter.to);
    }
    if (filter.subject) {
        search.append('subject', filter.subject);
    }
    if (filter.result) {
        search.append('result', filter.result);
    }
    if (filter.reservationId) {
        search.append('reservationId', filter.reservationId);
    }
    if (filter.emailStatus) {
        search.append('emailStatus', filter.emailStatus);
    }
    if (filter.logType) {
        search.append('logType', filter.logType);
    }

    const searchString = search.toString();
    return `?${searchString}`;
}

function EmaillogPage() {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const location = useLocation();
    const history = useHistory();
    const [currentFilters, setCurrentFilters] = useState(getFilterFromUrl(location.search));
    const [debouncedFilters, setDebouncedFilters] = useState(currentFilters);
    const [refreshCounter, setRefreshCounter] = useState(0);

    useEffect(() => {
        setCurrentFilters(getFilterFromUrl(location.search));
    }, [location.search]);

    useDebounce(
        () => {
            setDebouncedFilters(currentFilters);
            history.replace(getUrlFromFilter(currentFilters));
            setPage(0);
        },
        [currentFilters],
        700
    );

    const [data, loading, error] = useSwaggerClient(
        'emaillogs',
        'getEmaillogs',
        {
            page,
            pageSize,
            fromDate: moment(debouncedFilters.fromDate).isValid()
                ? moment(debouncedFilters.fromDate).format('YYYY-MM-DD')
                : '',
            toDate: moment(debouncedFilters.toDate).isValid()
                ? moment(debouncedFilters.toDate).format('YYYY-MM-DD')
                : '',
            to: debouncedFilters.to,
            subject: debouncedFilters.subject,
            result: debouncedFilters.result ? debouncedFilters.result : 'all',
            emailStatus: debouncedFilters.emailStatus || 'all',
            logType: debouncedFilters.logType || 'all',
            reservationId: debouncedFilters.reservationId
        },
        // refetch after a resend (or other mutation) bumps the counter
        [refreshCounter]
    );

    const resetFilters = useCallback(
        () =>
            setCurrentFilters({
                fromDate: null,
                toDate: null,
                to: '',
                subject: '',
                result: '',
                reservationId: '',
                emailStatus: '',
                logType: ''
            }),
        []
    );

    useEffect(() => {
        if (loading) {
            return;
        }
        if (data?.logs.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    if (error) {
        return (
            <Grid container justifyContent="center">
                <Alert className="mt-2" severity="error">
                    <h4>{error.response.body.message}</h4>
                </Alert>
            </Grid>
        );
    }

    return (
        <Grid container className="bg-white client-table page" alignItems="center" spacing={2}>
            <AdminHeader title="Emails" />
            <Grid item xs={12}>
                <FilterLine
                    possibleFilters={EMAIL_LOG_FILTERS}
                    filters={currentFilters}
                    setFilters={setCurrentFilters}
                    resetFilters={resetFilters}
                />
            </Grid>
            <Grid item container xs={12} justifyContent="center">
                <EmaillogTable
                    total={data?.count}
                    emails={data?.logs || []}
                    page={page}
                    pageSize={pageSize}
                    setPage={setPage}
                    setPageSize={setPageSize}
                    onResent={() => setRefreshCounter(c => c + 1)}
                />
            </Grid>
        </Grid>
    );
}

export default EmaillogPage;
